import { ChangeEvent, FunctionComponent, useCallback } from 'react';
import { Trans } from 'react-i18next';

import useElasticSearch from '../../hooks/useElasticSearch';
import ExinMuiCircularProgress from '../styled/ExinMuiCircularProgress';
import ExinMuiRadioGroup from '../styled/ExinMuiRadioGroup';
import { Certification } from './StepCertification';

/**
 * The user-chosen learning method. Represented by a string.
 */
export enum Method {
  TRAINING_EXAM = 'METHOD_TRAINING_EXAM',
  ELEARNING_EXAM = 'METHOD_ELEARNING_EXAM',
  SELFSTUDY_EXAM = 'METHOD_SELFSTUDY_EXAM',
  EXAM_ONLY = 'METHOD_EXAM_ONLY',
  VIRTUAL_CLASSROOM = 'METHOD_VIRTUAL_CLASSROOM',
}

export const methodMap = {
  [Method.ELEARNING_EXAM]: 1,
  [Method.TRAINING_EXAM]: 2,
  [Method.VIRTUAL_CLASSROOM]: 3,
};

export const OPTIONS = {
  [Method.TRAINING_EXAM]: {
    value: Method.TRAINING_EXAM,
    label: <Trans i18nKey="trainingAndExamLabel">Training and Exam</Trans>,
    tooltip: (
      <Trans i18nKey="trainingAndExamDescription">
        Follow an accredited classroom training and take your exam with an EXIN partner.
      </Trans>
    ),
    disabled: false,
  },
  [Method.ELEARNING_EXAM]: {
    value: Method.ELEARNING_EXAM,
    label: <Trans i18nKey="elearningAndExamLabel">eLearning and Exam</Trans>,
    tooltip: (
      <Trans i18nKey="elearningAndExamDescription">
        Follow an accredited online learning program and take your exam with an EXIN partner.
      </Trans>
    ),
    disabled: false,
  },
  [Method.VIRTUAL_CLASSROOM]: {
    value: Method.VIRTUAL_CLASSROOM,
    label: <Trans i18nKey="virtualClassroomLabel">Virtual Classroom Training and Exam</Trans>,
    tooltip: (
      <Trans i18nKey="virtualClassroomDescription">
        Follow an accredited virtual classroom training and take your exam with an EXIN partner. A
        virtual classroom training is done fully online.
      </Trans>
    ),
    disabled: false,
  },
  [Method.SELFSTUDY_EXAM]: {
    value: Method.SELFSTUDY_EXAM,
    label: <Trans i18nKey="selfstudyAndExamLabel">Self-study and Online Exam</Trans>,
    tooltip: (
      <Trans i18nKey="selfstudyAndExamDescription">
        Take care of acquiring your own study materials and prepare for your exam independently.
      </Trans>
    ),
    disabled: false,
  },
  [Method.EXAM_ONLY]: {
    value: Method.EXAM_ONLY,
    label: <Trans i18nKey="examOnlyLabel">Exam Only</Trans>,
    tooltip: (
      <Trans i18nKey="examOnlyDescription">
        Choose this option if you need to retake your exam or have followed an accredited training
        without an exam.
      </Trans>
    ),
    disabled: false,
  },
};

const transformServerResponse = (responseData: any): boolean => {
  return responseData?.hits?.total?.value === 1;
};

const StepCertificationMethod: FunctionComponent<{
  certification: Certification;
  trainingNeeded: boolean;
  selectedMethod: Method | null;
  setSelectedMethod(value: Method | null): void;
}> = ({ certification, selectedMethod, setSelectedMethod, trainingNeeded }) => {
  const { isLoading, response: hasCountries }: { isLoading: boolean; response: boolean | null } =
    useElasticSearch(
      {
        query: {
          bool: {
            must: [
              {
                term: {
                  'post_type.raw': 'ex_certification',
                },
              },
              {
                term: {
                  post_id: parseInt(certification.value, 10),
                },
              },
              {
                exists: {
                  field: 'exam_services',
                },
              },
            ],
          },
        },
        size: 1,
        _source: false,
      },
      transformServerResponse
    );

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value as Method;
      setSelectedMethod(value);
    },
    [setSelectedMethod]
  );

  if (isLoading) {
    return <ExinMuiCircularProgress />;
  }

  OPTIONS[Method.SELFSTUDY_EXAM].disabled = trainingNeeded || !hasCountries;
  OPTIONS[Method.EXAM_ONLY].disabled = !hasCountries;

  return (
    <ExinMuiRadioGroup
      value={selectedMethod}
      onChange={handleChange}
      options={Object.values(OPTIONS)}
    />
  );
};

export default StepCertificationMethod;
